import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { get_customer_coupons } from '../store/reducers/dashboardReducer';
import {
  FaTag,
  FaCopy,
  FaCheckCircle,
  FaShoppingCart,
  FaLock,
  FaTicketAlt,
  FaClock,
  FaFire,
} from 'react-icons/fa';

// ─── helpers ─────────────────────────────────────────────────────────────────

const formatDiscount = (coupon) =>
  coupon.type === 'fixed'
    ? `AED ${coupon.amount} off`
    : `${coupon.amount}% off`;

const formatExpiry = (date) => {
  if (!date) return null;
  return new Date(date).toLocaleDateString('en-AE', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

const isExpiringSoon = (date) => {
  if (!date) return false;
  const diff = new Date(date) - Date.now();
  return diff > 0 && diff < 3 * 24 * 60 * 60 * 1000; // within 3 days
};

const daysLeft = (date) => {
  if (!date) return null;
  const diff = new Date(date) - Date.now();
  if (diff <= 0) return null;
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
};

// ─── CouponCard ──────────────────────────────────────────────────────────────

const CouponCard = ({ coupon, onCopy, onUse, copied }) => {
  const isCopied = copied === coupon.code;
  const expiring = isExpiringSoon(coupon.expiry);
  const remaining = daysLeft(coupon.expiry);
  const expiryLabel = formatExpiry(coupon.expiry);

  return (
    <div className="relative rounded-2xl border border-dashed border-orange-200 bg-white overflow-hidden shadow-sm hover:shadow-md transition-shadow">

      {/* Expiring soon badge */}
      {expiring && (
        <div className="absolute top-3 right-3 flex items-center gap-1 bg-red-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
          <FaClock size={8} /> Ends in {remaining}d
        </div>
      )}

      {/* Discount strip */}
      <div className={`px-5 pt-5 pb-4 ${coupon.type === 'fixed' ? 'bg-gradient-to-r from-orange-500 to-orange-400' : 'bg-gradient-to-r from-slate-900 to-slate-700'}`}>
        <p className="text-white/70 text-[10px] font-semibold uppercase tracking-widest mb-1">
          {coupon.type === 'fixed' ? 'Fixed Discount' : 'Percentage Off'}
        </p>
        <p className="text-white text-3xl font-black leading-none">
          {formatDiscount(coupon)}
        </p>
        {coupon.minOrder > 0 && (
          <p className="text-white/70 text-[11px] mt-1.5">
            Min. order: AED {coupon.minOrder}
          </p>
        )}
      </div>

      {/* Divider with notches */}
      <div className="relative flex items-center">
        <div className="absolute -left-3 w-6 h-6 rounded-full bg-[#f7f7f7] border border-dashed border-orange-200" />
        <div className="flex-1 border-t border-dashed border-orange-200 mx-3" />
        <div className="absolute -right-3 w-6 h-6 rounded-full bg-[#f7f7f7] border border-dashed border-orange-200" />
      </div>

      {/* Code row */}
      <div className="px-5 pt-4 pb-3">
        <p className="text-[10px] font-semibold uppercase tracking-widest text-gray-400 mb-1">
          Coupon Code
        </p>
        <div className="flex items-center justify-between gap-3">
          <span className="font-black text-[18px] text-gray-900 font-mono tracking-widest">
            {coupon.code}
          </span>
          <button
            onClick={() => onCopy(coupon.code)}
            className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-[12px] font-bold transition-all shrink-0 ${
              isCopied
                ? 'bg-green-100 text-green-700 border border-green-200'
                : 'bg-orange-50 text-orange-600 border border-orange-200 hover:bg-orange-100'
            }`}
          >
            {isCopied
              ? <><FaCheckCircle size={10} /> Copied!</>
              : <><FaCopy size={10} /> Copy</>
            }
          </button>
        </div>
      </div>

      {/* Expiry + CTA */}
      <div className="px-5 pb-5 flex items-center justify-between gap-3">
        <p className="text-[11px] text-gray-400">
          {expiryLabel ? `Expires ${expiryLabel}` : 'No expiry date'}
        </p>
        <button
          onClick={() => onUse(coupon.code)}
          className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-black text-white text-[12px] font-bold hover:bg-orange-500 transition-colors shrink-0"
        >
          <FaShoppingCart size={10} /> Use at Checkout
        </button>
      </div>

    </div>
  );
};

// ─── LoginGate ───────────────────────────────────────────────────────────────

const LoginGate = () => (
  <div className="flex flex-col items-center justify-center py-20 text-center px-4">
    <div className="w-16 h-16 rounded-full bg-orange-50 border-2 border-orange-100 flex items-center justify-center mb-5">
      <FaLock className="text-orange-400" size={22} />
    </div>
    <h2 className="text-xl font-black text-gray-900 mb-2">Sign in to see your coupons</h2>
    <p className="text-gray-500 text-sm mb-6 max-w-xs">
      Create an account or sign in to access exclusive discount codes just for you.
    </p>
    <div className="flex gap-3">
      <Link
        to="/login"
        className="px-6 py-2.5 rounded-full bg-black text-white text-sm font-bold hover:bg-gray-800 transition"
      >
        Sign In
      </Link>
      <Link
        to="/register"
        className="px-6 py-2.5 rounded-full border-2 border-black text-black text-sm font-bold hover:bg-gray-50 transition"
      >
        Register
      </Link>
    </div>
  </div>
);

// ─── EmptyState ──────────────────────────────────────────────────────────────

const EmptyState = () => (
  <div className="flex flex-col items-center justify-center py-20 text-center">
    <div className="w-16 h-16 rounded-full bg-gray-50 border-2 border-gray-100 flex items-center justify-center mb-5">
      <FaTicketAlt className="text-gray-300" size={22} />
    </div>
    <h2 className="text-lg font-bold text-gray-700 mb-1">No coupons available right now</h2>
    <p className="text-gray-400 text-sm">Check back later — exclusive deals are added regularly.</p>
  </div>
);

// ─── Filter tabs ─────────────────────────────────────────────────────────────

const FILTERS = [
  { key: 'all', label: 'All Offers' },
  { key: 'percentage', label: '% Off' },
  { key: 'fixed', label: 'AED Off' },
  { key: 'expiring', label: 'Expiring Soon' },
];

// ─── Main page ────────────────────────────────────────────────────────────────

const Offers = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { userInfo } = useSelector((s) => s.auth);
  const { coupons } = useSelector((s) => s.dashboard);

  const [copied, setCopied] = useState(null);
  const [activeFilter, setActiveFilter] = useState('all');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!userInfo?.id) return;
    setLoading(true);
    dispatch(get_customer_coupons()).finally(() => setLoading(false));
  }, [userInfo?.id, dispatch]);

  const handleCopy = (code) => {
    navigator.clipboard.writeText(code).catch(() => {});
    setCopied(code);
    setTimeout(() => setCopied(null), 2500);
  };

  const handleUse = (code) => {
    navigate('/card', { state: { prefillCoupon: code } });
  };

  const filtered = coupons.filter((c) => {
    if (activeFilter === 'percentage') return c.type === 'percentage';
    if (activeFilter === 'fixed') return c.type === 'fixed';
    if (activeFilter === 'expiring') return isExpiringSoon(c.expiry);
    return true;
  });

  return (
    <div className="min-h-screen bg-[#f7f7f7]">
      <Header />

      {/* Hero banner */}
      <div className="bg-gradient-to-r from-slate-900 via-gray-900 to-slate-800 text-white">
        <div className="mx-auto max-w-[1200px] px-6 py-12 flex flex-col items-center text-center gap-3">
          <div className="flex items-center gap-2 bg-orange-500/20 border border-orange-500/30 px-4 py-1.5 rounded-full text-orange-400 text-[12px] font-bold">
            <FaFire size={11} /> Exclusive Member Offers
          </div>
          <h1 className="text-3xl md:text-4xl font-black">
            Your Discount Coupons
          </h1>
          <p className="text-gray-400 text-sm max-w-md">
            Apply any coupon at checkout for instant savings. Codes are automatically validated before your order is placed.
          </p>
          <Link
            to="/card"
            className="mt-2 flex items-center gap-2 bg-orange-500 hover:bg-orange-400 text-white font-bold text-sm px-6 py-2.5 rounded-full transition"
          >
            <FaShoppingCart size={13} /> Go to Cart
          </Link>
        </div>
      </div>

      <div className="mx-auto max-w-[1200px] px-6 py-8">

        {!userInfo ? (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100">
            <LoginGate />
          </div>
        ) : (
          <>
            {/* Filter tabs */}
            <div className="flex flex-wrap gap-2 mb-6">
              {FILTERS.map((f) => (
                <button
                  key={f.key}
                  onClick={() => setActiveFilter(f.key)}
                  className={`px-4 py-2 rounded-full text-[13px] font-semibold transition ${
                    activeFilter === f.key
                      ? 'bg-black text-white'
                      : 'bg-white text-gray-600 border border-gray-200 hover:border-gray-400'
                  }`}
                >
                  {f.label}
                  {f.key === 'expiring' && coupons.filter(c => isExpiringSoon(c.expiry)).length > 0 && (
                    <span className="ml-1.5 bg-red-500 text-white text-[10px] px-1.5 py-0.5 rounded-full">
                      {coupons.filter(c => isExpiringSoon(c.expiry)).length}
                    </span>
                  )}
                </button>
              ))}
              <span className="ml-auto text-[13px] text-gray-400 self-center">
                {filtered.length} coupon{filtered.length !== 1 ? 's' : ''}
              </span>
            </div>

            {/* Loading skeleton */}
            {loading && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="rounded-2xl bg-white border border-gray-100 overflow-hidden animate-pulse">
                    <div className="h-28 bg-gray-200" />
                    <div className="p-5 space-y-3">
                      <div className="h-4 bg-gray-100 rounded w-2/3" />
                      <div className="h-7 bg-gray-100 rounded w-1/2" />
                      <div className="h-8 bg-gray-100 rounded-full w-full" />
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Coupon grid */}
            {!loading && filtered.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {filtered.map((coupon) => (
                  <CouponCard
                    key={coupon._id}
                    coupon={coupon}
                    onCopy={handleCopy}
                    onUse={handleUse}
                    copied={copied}
                  />
                ))}
              </div>
            )}

            {/* Empty state */}
            {!loading && filtered.length === 0 && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100">
                <EmptyState />
              </div>
            )}

            {/* How it works */}
            <div className="mt-10 bg-white rounded-2xl border border-gray-100 shadow-sm px-6 py-7">
              <h2 className="text-[15px] font-black text-gray-900 mb-5">How to use a coupon</h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                {[
                  { step: '1', icon: FaCopy, title: 'Copy the code', desc: 'Click Copy on any coupon card above.' },
                  { step: '2', icon: FaShoppingCart, title: 'Go to your cart', desc: 'Add items and proceed to your cart.' },
                  { step: '3', icon: FaCheckCircle, title: 'Apply at checkout', desc: 'Paste the code in the coupon field and click Apply.' },
                ].map(({ step, icon: Icon, title, desc }) => (
                  <div key={step} className="flex gap-4">
                    <div className="w-8 h-8 rounded-full bg-orange-500 text-white text-sm font-black flex items-center justify-center shrink-0">
                      {step}
                    </div>
                    <div>
                      <p className="font-bold text-gray-900 text-sm">{title}</p>
                      <p className="text-gray-500 text-[12px] mt-0.5">{desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

      </div>

      <Footer />
    </div>
  );
};

export default Offers;
